<template>
  <section
    v-if="expanded"
    class="cma-asset-info-panel"
    :class="{ 'cma-asset-info-panel--pinned': pinned }"
  >
    <div class="cma-asset-info-panel__header">
      <span class="cma-asset-info-panel__title">{{ $t('cma.asset-info.title') }}</span>

      <!-- Botão de fixar (pin) removido de tela por enquanto, a pedido de
      produto — comentado, não excluído: lógica (pinned/togglePin/
      PINNED_STORAGE_KEY) segue intacta para religar depois.
      <button
        type="button"
        class="cma-asset-info-panel__pin"
        :class="{ 'cma-asset-info-panel__pin--active': pinned }"
        :aria-pressed="String(pinned)"
        :aria-label="$t(pinLabelKey)"
        :title="$t(pinLabelKey)"
        @click.stop="togglePin"
      >
        <span
          class="material-icons-round"
          aria-hidden="true"
        >push_pin</span>
      </button>
      -->

      <button
        type="button"
        class="cma-asset-info-panel__close"
        :aria-label="$t('cma.asset-info.close')"
        :title="$t('cma.asset-info.close')"
        @click.stop="close"
      >
        <span
          class="material-icons-round"
          aria-hidden="true"
        >close</span>
      </button>
    </div>

    <div class="cma-asset-info-panel__body">
      <div
        v-for="item in items"
        :key="item.id"
        class="cma-asset-info-panel__item"
      >
        <span class="cma-asset-info-panel__label">{{ item.label || $t(item.labelKey) }}</span>
        <span
          v-if="loading"
          class="cma-skeleton-bar cma-asset-info-panel__value-skeleton"
        />
        <span
          v-else
          class="cma-asset-info-panel__value"
        >{{ itemValue(item) }}</span>
      </div>
    </div>
  </section>
</template>

<script>
import { buildAssetInfoItems, buildCoolingStageItems } from '@/domain/data/cma/assetInfoItems';

const EMPTY_VALUE = '—';

const PINNED_STORAGE_KEY = 'cma-asset-info-pinned';

function readPinned() {
  try {
    return localStorage.getItem(PINNED_STORAGE_KEY) === 'true';
  } catch (e) {
    return false;
  }
}

function writePinned(pinned) {
  try {
    localStorage.setItem(PINNED_STORAGE_KEY, String(pinned));
  } catch (e) {
    // localStorage pode lancar (navegacao privada, cookies bloqueados em
    // iframe); a preferencia e conforto de tela, entao segue sem gravar
  }
}

export default {
  name: 'CmaAssetInfoPanel',
  props: {
    transformerData: {
      type: Object,
      default: null,
    },
    registrationData: {
      type: Object,
      default: null,
    },
    coolingStagesData: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    /*
     * Quem abre e fecha é o ícone no cabeçalho do dashboard (ver
     * CmaDashboard.vue, __asset-info-button): o painel não tem mais barra
     * colapsada própria, então o estado mora no pai e chega por aqui.
     */
    expanded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { pinned: readPinned() };
  },
  created() {
    // fixado é "já vem aberto": avisa o pai, que é o dono do estado
    if (this.pinned) this.$emit('toggle', true);
  },
  computed: {
    pinLabelKey() {
      return this.pinned ? 'cma.asset-info.unpin' : 'cma.asset-info.pin';
    },
    items() {
      // O idioma do luxon (formato de data) e do translate() não é reativo;
      // ler o locale do i18n faz os textos serem refeitos ao trocar de idioma.
      // eslint-disable-next-line no-unused-expressions
      this.$i18n.locale;
      return [
        ...buildAssetInfoItems({
          transformerData: this.transformerData,
          registrationData: this.registrationData,
          coolingStagesData: this.coolingStagesData,
        }),
        ...buildCoolingStageItems(this.coolingStagesData),
      ];
    },
  },
  methods: {
    /* Fechar cancela o "vem aberto": deixar fixado e fechado faria o painel
     * reaparecer sozinho no próximo carregamento, contra o último gesto. */
    close() {
      if (this.pinned) {
        this.pinned = false;
        writePinned(false);
      }
      this.$emit('toggle', false);
    },
    togglePin() {
      this.pinned = !this.pinned;
      writePinned(this.pinned);
    },
    itemValue(item) {
      if (item.valueKey) return this.$t(item.valueKey);
      if (item.text) return item.text;
      if (typeof item.boolean === 'boolean') return this.$t(item.boolean ? 'general.yes' : 'general.no');
      return EMPTY_VALUE;
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"

.cma-asset-info-panel
  display: flex
  flex-direction: column
  min-height: 0
  background-color: var(--color-background-light)
  border: 1px solid var(--color-border-dark)
  border-radius: 8px
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35)
  overflow: hidden

  &__header
    display: flex
    align-items: center
    gap: 0.35rem
    width: 100%
    padding: 0.85rem 1rem
    color: var(--font-color-primary)
    flex-shrink: 0

    +media-min-3xl()
      padding: 1rem 1.3rem

    +media-min-4xl()
      padding: 1.15rem 1.5rem

  &__pin
    display: inline-flex
    align-items: center
    // empurrava o par fixar/fechar para a direita, com o título à esquerda —
    // mantido aqui para quando o botão voltar (ver comentário no template),
    // mas quem faz esse trabalho sozinho por ora é o `margin-left: auto` do
    // `&__close` logo abaixo.
    padding: 0
    border: none
    background: none
    color: var(--font-color-secondary)
    cursor: pointer
    flex-shrink: 0

    .material-icons-round
      font-size: 1.15rem
      transform: rotate(45deg)
      transition: transform 0.2s ease

    &--active
      color: var(--cma-color-highlight)

      .material-icons-round
        transform: none

    +media-min-3xl()
      .material-icons-round
        font-size: 1.35rem

  &__title
    font-size: 0.85rem
    font-weight: 700
    letter-spacing: 0.025em
    text-align: left

    +media-min-3xl()
      font-size: 1.05rem

    +media-min-4xl()
      font-size: 1.25rem

  &__close
    display: inline-flex
    align-items: center
    // empurra o botão para a ponta direita do header, longe do título — com
    // o pin comentado (ver template), é o único irmão depois do título, então
    // o auto sozinho já resolve sem depender do `&__pin` estar no DOM.
    margin-left: auto
    padding: 0
    border: none
    background: none
    color: var(--font-color-secondary)
    cursor: pointer
    flex-shrink: 0

    &:hover
      color: var(--font-color-primary)

    .material-icons-round
      font-size: 1.25rem

      +media-min-3xl()
        font-size: 1.5rem

      +media-min-4xl()
        font-size: 1.75rem

  &__body
    display: flex
    flex-direction: column
    gap: 0.5rem
    padding: 0 1rem 1rem
    overflow-y: auto
    min-height: 0

    +media-min-3xl()
      padding: 0 1.3rem 1.3rem

    +media-min-4xl()
      padding: 0 1.5rem 1.5rem

  &__item
    display: flex
    flex-direction: column
    gap: 0.15rem
    padding-top: 0.5rem
    border-top: 1px solid var(--color-border-dark)

    &:first-of-type
      padding-top: 0
      border-top: none

  &__label
    color: var(--font-color-secondary)
    font-size: 0.7rem
    letter-spacing: 0.05em
    text-transform: uppercase

    +media-min-3xl()
      font-size: 0.9rem

    +media-min-4xl()
      font-size: 1.1rem

  &__value
    color: var(--font-color-primary)
    font-weight: 700
    font-size: 0.85rem

    +media-min-3xl()
      font-size: 1.05rem

    +media-min-4xl()
      font-size: 1.25rem

  &__value-skeleton
    width: 60%
    height: 0.85rem

    +media-min-3xl()
      height: 1.05rem

    +media-min-4xl()
      height: 1.25rem
</style>
